<template>
  <div v-if="statistics && isAvailable">
    <project-counter
      class="statistic"
      :statistics="statistics"
      :statistic="statistic"
      :show-statistic="block.isShowed('statistic')"
    >
      <template
        #value
        v-if="statistic === 'duration'"
      >
        {{ daysLeft }}
      </template>

      <template
        #tooltip
        v-if="statistic === 'amount_collected' && currency !== defaultCurrency"
      >
        <app-tooltip>
          <button
            class="tooltip"
            :aria-describedby="`${block.id}_tooltip`"
            :aria-label="t('literal.show_amount_collected_in_project_currency')"
          >
            <fa-icon :icon="['fal', 'coins'] " />
          </button>

          <template #popper>
            <p
              :id="`${block.id}_tooltip`"
              role="tooltip"
            >
              <app-money
                :amount="amount"
                :currency="defaultCurrency"
              />

              <template v-if="goal">
                /
                <app-money
                  :amount="goal"
                  :currency="defaultCurrency"
                />
              </template>
            </p>
          </template>
        </app-tooltip>
      </template>

      <template
        #text="{ value }"
        v-if="block.isShowed(`text_${text}`)"
      >
        <template v-if="statistic === 'duration'">
          {{ plural(block.getText(text), daysLeft) }}
        </template>

        <template v-else-if="isTextDisplayed">
          {{ plural(block.getText(text), value, variables) }}
        </template>
      </template>
    </project-counter>

    <app-progress-bar
      :value="amount"
      :max-value="goal"
      v-if="block.isShowed('progressbar') && goal"
    />
  </div>
</template>

<script setup>
import { computed, defineAsyncComponent, inject } from 'vue'
import { storeToRefs } from 'pinia'
import { useCurrencyStore } from '@/stores/CurrencyStore'
import { useI18n } from '@/vendors/i18n'
import { useMoney } from '@/composables/app/useMoney'
import { get as _get } from 'lodash'
import { isFuture, differenceInCalendarDays } from 'date-fns'

import ProjectCounter from '@/components/modules/project/ProjectCounter'
const AppMoney = defineAsyncComponent(() => import('&/atoms/AppMoney'))
const AppTooltip = defineAsyncComponent(() => import('&/atoms/AppTooltip'))
const AppProgressBar = defineAsyncComponent(() => import('&/atoms/AppProgressBar'))

const { block } = defineProps({
  column: { type: Object, required: true },
  block: { type: Object, required: true }
})

const { currency } = storeToRefs(useCurrencyStore())
const { t, translate, plural } = useI18n()
const { getAmount, getFormatedAmount } = useMoney()

const objects = inject('objects', {})

const { statistic, text } = block.settings

const project = computed(() => _get(objects.value, 'project'))
const statistics = computed(() => _get(objects.value, 'statistics', {}))

const defaultCurrency = computed(() => project.value?.currency)
const startDate = computed(() => project.value?.start_date ? new Date(project.value?.start_date) : null)
const endDate = computed(() => project.value?.end_date ? new Date(project.value.end_date) : null)
const goalAmount = computed(() => project.value?.goal_amount)
const amountCollected = computed(() => statistics.value?.amount_collected)

const amount = computed(() => getAmount({ amount: amountCollected, currency: defaultCurrency }))
const goal = computed(() => getAmount({ amount: goalAmount, currency: defaultCurrency }))

const daysLeft = computed(() => {
  const difference = differenceInCalendarDays(endDate.value, new Date())

  return difference > 0 ? difference : 0
})

const variables = computed(() => ({ goal: getFormatedAmount({ amount: goalAmount, currency }) }))

const isTextDisplayed = computed(() => text !== 'amount' || goal.value || !translate(block.getText(text))?.includes('%%goal%%'))

const isAvailable = computed(() => {
  if (statistic === 'duration' && (!endDate.value || isFuture(startDate.value))) return false

  return block.isShowed('statistic') || block.isShowed(`text_${text}`) || (block.isShowed('progressbar') && goal.value)
})
</script>

<style lang="scss" scoped>
.statistic {
  color: rgba($dg, .6);
}

.tooltip {
  font-size: rem(24px);
  color: $vlg;
}

.progressbar.progressbar {
  margin-top: 12px;
}
</style>